import {
  forwardRef,
  type CSSProperties,
  type HTMLAttributes,
  type ReactNode,
} from "react";
import { cn } from "@/lib/cn";
import {
  menuSurfaceProps,
  useMenuSurface,
  type MenuSurface,
} from "./menu-surface";

export type PocketPanelProps = {
  /** Native glass group, e.g. "tray-panel", "settings-pocket", "search-hub". */
  nativeGroup: string;
  /** Use the theme CSS fallback while an anchored pocket follows the canvas. */
  nativeSurface?: boolean;
  /** Its material; by default the one its surface declares. */
  surface?: MenuSurface;
  "data-denker-glass-force"?: string;
  testId: string;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
} & Omit<HTMLAttributes<HTMLDivElement>, "className" | "style" | "children">;

/**
 * PocketPanel — the compact-surface panel (compact surface standard §3–§4):
 * the menu material (surface role `menu`; the role owns the 24px radius and
 * the theme's CSS fallback), 12px padding, and native glass registered as an
 * island in `nativeGroup`. The tray, the Settings pocket and the search hub
 * render through it; callers add size and position. On the card material
 * it wears the wall card instead, with no glass registration.
 */
export const PocketPanel = forwardRef<HTMLDivElement, PocketPanelProps>(
  function PocketPanel(
    {
      nativeGroup,
      nativeSurface = true,
      surface,
      testId,
      className,
      style,
      children,
      "data-denker-glass-force": glassForce,
      ...rest
    },
    ref,
  ) {
    const material = useMenuSurface(surface);
    // A card pocket is CSS-painted: no glass group, no native plate.
    const native = material === "glass" && nativeSurface;
    const paint = menuSurfaceProps(
      material,
      native ? { nativeLevel: "island", nativeGroup } : {},
    );
    return (
      <div
        ref={ref}
        {...rest}
        className={cn(
          paint.className,
          // Marks a pocket for CSS that sets a surface's ink round it
          // (a pinned widget's frame, `desktop-pin.css`).
          "denker-pocket flex flex-col p-3 text-primary",
          material === "glass" && "border",
          className,
        )}
        style={style}
        data-testid={testId}
        {...paint.attributes}
        data-denker-native-level={native ? "island" : undefined}
        data-denker-native-group={
          native ? nativeGroup.trim() || undefined : undefined
        }
        data-denker-glass-force={native ? glassForce : "css"}
      >
        {children}
      </div>
    );
  },
);
